import {
  BenchmarkCurve,
  DealIconType,
  DealVersion,
  FollowCellClickAction
} from '../..';
import { DealUserRole, Maturity } from './deal.types';
import { DealSummaryGridColumnName } from '../grid/types/column.types';
import { ImpliedYieldValue } from '../grid/utils/value-getters/implied-yield';
import { ImpliedSpreadValue } from '../grid/utils/value-getters/implied-spread';
import { TesValue } from '../grid/utils/value-getters/maturity-getter';
import { BenchmarkValue } from '../grid/utils/value-getters/benchmark-spread-getter';
import { Observable } from 'rxjs';
import { Moment } from 'moment';

export interface SpreadViewModel {
  benchmarkCurve?: BenchmarkCurve;
}

export interface DealGridViewModel extends DealVersion {
  icon?: DealIconType;
  clickAction?: FollowCellClickAction;
}

export interface CalendarGridViewModel extends DealGridViewModel {
  benchmarkCurve?: BenchmarkCurve;
}

export interface MonitorGridViewModel extends DealVersion {
  icon?: DealIconType;
  clickAction?: FollowCellClickAction;

  analysts: string[];
}

export interface ManagerGridViewModel extends DealVersion {
  managementStatus: DealManagementStatus;
  userRole: DealUserRole;
  amount?: number;
}

export interface MaturityBenchmarkPart {
  [DealSummaryGridColumnName.BenchmarkSpread]: BenchmarkValue;
}

export interface MaturityAnalyticsPart {
  [DealSummaryGridColumnName.ImpliedYield]: ImpliedYieldValue;
  [DealSummaryGridColumnName.ImpliedSpread]: ImpliedSpreadValue;
  [DealSummaryGridColumnName.TaxEqYld]: string;
  [DealSummaryGridColumnName.TaxEqSpread]: TesValue;
  [DealSummaryGridColumnName.OAS]: number;
  [DealSummaryGridColumnName.Duration]: number;
}

export interface MaturityPricingPart {
  [DealSummaryGridColumnName.Bid]: number;
  [DealSummaryGridColumnName.Ask]: number;
  [DealSummaryGridColumnName.BidYTW]: number;
  [DealSummaryGridColumnName.PricingDateTime]: Moment;
  [DealSummaryGridColumnName.LiquidityScore]: number;
  [DealSummaryGridColumnName.HistoricalPricing]?: boolean;
}

export type MaturityAdditionalPart =
  | MaturityBenchmarkPart
  | MaturityAnalyticsPart
  | MaturityPricingPart;

export enum MaturityAdditionalPartName {
  MaturityBenchmarkPart = 'MaturityBenchmarkPart',
  MaturityAnalyticsPart = 'MaturityAnalyticsPart',
  MaturityPricingPart = 'MaturityPricingPart'
}

export interface MaturityAdditionalPartRequest {
  name: MaturityAdditionalPartName;
  // Some columns such as impliedSpread TES
  // have parameterized values.
  // We store params for them in the deal-show -> chart-params store slice
  // separately for each deal show tab
  chartParamsSlice: ChartType;
}

export type GridMaturityModel = Maturity & MaturityBenchmarkPart;

export type GridMaturityModelWithAnalytics = GridMaturityModel &
  MaturityAnalyticsPart;

export type PricingGridMaturityModel = Maturity & MaturityPricingPart;

export type MaturityViewModel =
  | GridMaturityModel
  | GridMaturityModelWithAnalytics
  | PricingGridMaturityModel;

export type MaturityPartExtractor<T extends unknown> = (
  maturity: Maturity
) => T;

export enum ChartType {
  SummaryInfo = 'summaryInfo',
  BidInfo = 'bidinfo',
  BidsCompare = 'bidscompare',
  AnalyticsInfo = 'analyticsInfo',
  PricingInfo = 'pricingInfo'
}

export enum DealManagementStatus {
  LeadManaged = 'leadManaged',
  CoManaged = 'coManaged',
  NoRole = 'noRole'
}

export enum BidStatus {
  LiveBids = 'liveBids',
  BidsWon = 'bidsWon',
  BidsLost = 'bidsLost',
  BidsNotSubmitted = 'bidsNotSubmitted'
}

export enum BidStatusName {
  liveBids = 'Live Bids',
  bidsWon = 'Bids Won',
  bidsLost = 'Bids Lost',
  bidsNotSubmitted = 'Unsubmitted'
}

export interface MaturityAdditionalPartProvider<
  T extends unknown,
  U extends unknown
> {
  additionalPartName: MaturityAdditionalPartName;
  getRequiredContextObservable(
    chartType: ChartType,
    chartMaturities?: Maturity[]
  ): Observable<T>;
  valuesProvider(maturity: Maturity, context: T): U;
}
